/**
 * 发版公告轮询：跨页签领头、BroadcastChannel、inFlight、localStorage 短缓存。
 * 主应用与维护页共用（见 releaseNoticePollClient.entry.ts）。
 * 另提供 checkReleaseNoticeOnUserAction：操作触发探测（预告门禁）。
 */

import { shouldSkipActionFetchForDismissedBanner } from "./releaseNoticeDismiss";

export type ReleaseNoticePollTier = "idle" | "banner" | "maintenance";

export type ReleaseNoticeActive = {
  projectCode?: string;
  hasBanner?: boolean;
  bannerText?: string;
  deployStartAt?: string;
  deployEndAt?: string;
  inMaintenance?: boolean;
  targetVersion?: string;
  description?: string;
  maintenanceTitle?: string;
  estimatedDurationText?: string;
  maintenancePageUrl?: string;
  officialSiteUrl?: string;
  pollTier?: ReleaseNoticePollTier;
  pollIdleMinutes?: number;
  pollBannerMinutes?: number;
  pollMaintenanceMinutes?: number;
  nearMaintenanceLeadMinutes?: number;
  serverCacheSeconds?: number;
};

type CachedPayload = {
  notice: ReleaseNoticeActive | null;
  fetchedAt: number;
};

type LeaderLease = {
  tabId: string;
  until: number;
};

type ChannelMessage =
  | { type: "notice"; notice: ReleaseNoticeActive | null; fetchedAt: number }
  | { type: "sync_request" };

const CHANNEL_NAME = "eweadn_release_notice_v1";
const CACHE_STORAGE_KEY = "eweadn_release_notice_cache_v1";
const LEADER_STORAGE_KEY = "eweadn_release_notice_leader_v1";
const LEADER_LEASE_MS = 12_000;

/** 接口未下发间隔时的兜底（分钟），与 hub 默认一致：720 / 5 / 2 */
const DEFAULT_IDLE_MIN = 720;
const DEFAULT_BANNER_MIN = 5;
const DEFAULT_MAINTENANCE_MIN = 2;

const TAB_ID =
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`;

export type ReleaseNoticePollingMode = "app" | "maintenance";

export type StartReleaseNoticePollingOptions = {
  projectCode: string;
  mode: ReleaseNoticePollingMode;
  /** maintenance 模式：维护结束跳转首页 */
  homeUrl?: string;
  onNotice?: (notice: ReleaseNoticeActive | null) => void;
};

let inflight: Promise<ReleaseNoticeActive | null> | null = null;
let memoryCache: CachedPayload | null = null;
let channel: BroadcastChannel | null = null;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollingOptions: StartReleaseNoticePollingOptions | null = null;
let pollingProjectCode: string | null = null;
let leaderHeartbeatTimer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<(notice: ReleaseNoticeActive | null) => void>();

function readStorageCache(): CachedPayload | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(CACHE_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CachedPayload;
    if (!parsed || typeof parsed.fetchedAt !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStorageCache(payload: CachedPayload): void {
  memoryCache = payload;
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(CACHE_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    /* ignore */
  }
}

function getEffectiveCache(): CachedPayload | null {
  return memoryCache ?? readStorageCache();
}

function resolvePollIntervalMs(notice: ReleaseNoticeActive | null): number {
  const tier = notice?.pollTier ?? "idle";
  const idle = (notice?.pollIdleMinutes ?? DEFAULT_IDLE_MIN) * 60_000;
  const banner = (notice?.pollBannerMinutes ?? DEFAULT_BANNER_MIN) * 60_000;
  const maintenance =
    (notice?.pollMaintenanceMinutes ?? DEFAULT_MAINTENANCE_MIN) * 60_000;
  if (tier === "maintenance") return maintenance;
  if (tier === "banner") return banner;
  return idle;
}

function shouldForceNetworkRefresh(): boolean {
  const cached = getEffectiveCache();
  return !isCacheFresh(cached, cached?.notice ?? null);
}

function isCacheFresh(
  payload: CachedPayload | null,
  notice: ReleaseNoticeActive | null
): boolean {
  if (!payload) return false;
  const maxAge = resolvePollIntervalMs(notice ?? payload.notice);
  return Date.now() - payload.fetchedAt < maxAge;
}

function readLeaderLease(): LeaderLease | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(LEADER_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as LeaderLease;
    if (!parsed?.tabId || typeof parsed.until !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLeaderLease(): void {
  if (typeof localStorage === "undefined") return;
  try {
    const lease: LeaderLease = {
      tabId: TAB_ID,
      until: Date.now() + LEADER_LEASE_MS,
    };
    localStorage.setItem(LEADER_STORAGE_KEY, JSON.stringify(lease));
  } catch {
    /* ignore */
  }
}

function isLeaderTab(): boolean {
  const lease = readLeaderLease();
  if (!lease || lease.until < Date.now()) {
    writeLeaderLease();
    return true;
  }
  return lease.tabId === TAB_ID;
}

function ensureLeaderHeartbeat(): void {
  if (leaderHeartbeatTimer != null) return;
  leaderHeartbeatTimer = setInterval(() => {
    if (isLeaderTab()) {
      writeLeaderLease();
    }
  }, LEADER_LEASE_MS / 2);
}

function ensureChannel(): BroadcastChannel | null {
  if (channel) return channel;
  if (typeof BroadcastChannel === "undefined") return null;
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (ev: MessageEvent<ChannelMessage>) => {
      const msg = ev.data;
      if (!msg || typeof msg !== "object") return;
      if (msg.type === "notice") {
        writeStorageCache({ notice: msg.notice, fetchedAt: msg.fetchedAt });
        notifyListeners(msg.notice);
      }
      if (msg.type === "sync_request" && isLeaderTab()) {
        const cached = getEffectiveCache();
        if (cached) {
          broadcastNotice(cached.notice, cached.fetchedAt);
        }
      }
    };
    try {
      channel.postMessage({ type: "sync_request" } satisfies ChannelMessage);
    } catch {
      /* ignore */
    }
    return channel;
  } catch {
    return null;
  }
}

function broadcastNotice(
  notice: ReleaseNoticeActive | null,
  fetchedAt: number
): void {
  ensureChannel()?.postMessage({
    type: "notice",
    notice,
    fetchedAt,
  } satisfies ChannelMessage);
}

function notifyListeners(notice: ReleaseNoticeActive | null): void {
  for (const fn of listeners) {
    try {
      fn(notice);
    } catch {
      /* ignore */
    }
  }
}

async function fetchActiveReleaseNoticeFromNetwork(
  projectCode: string
): Promise<ReleaseNoticeActive | null> {
  try {
    const res = await fetch(
      `/api/release-notices/active?projectCode=${encodeURIComponent(projectCode)}`,
      { credentials: "same-origin" }
    );
    if (!res.ok) return null;
    const json = (await res.json()) as {
      data?: ReleaseNoticeActive;
    };
    return json.data ?? null;
  } catch {
    return null;
  }
}

export async function refreshReleaseNotice(
  projectCode: string,
  options?: { force?: boolean }
): Promise<ReleaseNoticeActive | null> {
  const cached = getEffectiveCache();
  if (!options?.force && cached && isCacheFresh(cached, cached.notice)) {
    memoryCache = cached;
    return cached.notice;
  }

  if (!options?.force && !isLeaderTab()) {
    ensureChannel();
    return cached?.notice ?? null;
  }

  if (inflight) return inflight;

  inflight = fetchActiveReleaseNoticeFromNetwork(projectCode)
    .then((notice) => {
      const payload: CachedPayload = { notice, fetchedAt: Date.now() };
      writeStorageCache(payload);
      broadcastNotice(notice, payload.fetchedAt);
      notifyListeners(notice);
      return notice;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
}

function isMaintenanceDocument(): boolean {
  return (
    typeof document !== "undefined" &&
    document.documentElement?.getAttribute("data-maintenance-page") === "1"
  );
}

function htmlLooksLikeMaintenancePage(html: string): boolean {
  return (
    html.includes('data-maintenance-page="1"') ||
    html.includes("E HUB 升级维护中") ||
    html.includes("data-mode=\"maintenance\"")
  );
}

let leavingMaintenance = false;

/**
 * 维护结束离场：先探测首页是否仍被 nginx `.maintenance` 内部重写到本页。
 * 若仍是维护 HTML 则不跳转，避免 replace('/') 整页死循环刷新。
 */
async function tryLeaveMaintenancePage(homeUrl: string): Promise<void> {
  if (leavingMaintenance) return;
  leavingMaintenance = true;
  try {
    const res = await fetch(homeUrl, {
      cache: "no-store",
      credentials: "same-origin",
      headers: { Accept: "text/html" },
    });
    if (!res.ok) return;
    const html = await res.text();
    if (htmlLooksLikeMaintenancePage(html)) {
      return;
    }
    window.location.replace(homeUrl);
  } catch {
    /* 探测失败则留在维护页，等下一轮轮询 */
  } finally {
    leavingMaintenance = false;
  }
}

/**
 * 维护页离场：仅在接口明确 inMaintenance === false 时尝试回首页。
 * fetch 失败（notice == null）不离场，避免误跳。
 */
function applyMaintenanceRedirect(
  notice: ReleaseNoticeActive | null,
  options: StartReleaseNoticePollingOptions
): void {
  if (options.mode === "maintenance") {
    if (notice && notice.inMaintenance === false && options.homeUrl) {
      void tryLeaveMaintenancePage(options.homeUrl);
    }
    return;
  }
  if (
    notice?.inMaintenance &&
    notice.maintenancePageUrl &&
    !window.location.pathname.includes("/releaseTips/") &&
    !isMaintenanceDocument()
  ) {
    const url = new URL(notice.maintenancePageUrl, window.location.origin);
    if (notice.maintenanceTitle) {
      url.searchParams.set("title", notice.maintenanceTitle);
    }
    if (notice.estimatedDurationText) {
      url.searchParams.set("duration", notice.estimatedDurationText);
    }
    if (notice.officialSiteUrl) {
      url.searchParams.set("official", notice.officialSiteUrl);
    }
    if (notice.description) {
      url.searchParams.set("desc", notice.description);
    }
    window.location.replace(`${url.pathname}${url.search}`);
  }
}

function scheduleNextPoll(
  projectCode: string,
  options: StartReleaseNoticePollingOptions
): void {
  pollingProjectCode = projectCode;
  pollingOptions = options;
  if (pollTimer != null) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }

  const cached = getEffectiveCache();
  const intervalMs = resolvePollIntervalMs(cached?.notice ?? null);

  pollTimer = setTimeout(() => {
    void runPollTick();
  }, intervalMs);
}

async function runPollTick(): Promise<void> {
  const projectCode = pollingProjectCode;
  const options = pollingOptions;
  if (!projectCode || !options) return;

  if (
    typeof document !== "undefined" &&
    document.visibilityState !== "visible"
  ) {
    scheduleNextPoll(projectCode, options);
    return;
  }

  if (!isLeaderTab()) {
    ensureChannel()?.postMessage({
      type: "sync_request",
    } satisfies ChannelMessage);
    scheduleNextPoll(projectCode, options);
    return;
  }

  const notice = await refreshReleaseNotice(projectCode, {
    force: shouldForceNetworkRefresh(),
  });
  options.onNotice?.(notice);
  applyMaintenanceRedirect(notice, options);
  scheduleNextPoll(projectCode, options);
}

export function subscribeReleaseNotice(
  listener: (notice: ReleaseNoticeActive | null) => void
): () => void {
  listeners.add(listener);
  const cached = getEffectiveCache();
  if (cached) {
    listener(cached.notice);
  }
  return () => {
    listeners.delete(listener);
  };
}

const DEFAULT_PROJECT_CODE = "eweadn-qiankun";

/**
 * 用户关键操作触发的探测：连设备、切路由、进子应用、进神光同步等。
 * 预告期内且已关弹窗 → 跳过请求；否则强制拉接口，命中维护则跳转。
 */
export async function checkReleaseNoticeOnUserAction(
  projectCode: string = DEFAULT_PROJECT_CODE
): Promise<ReleaseNoticeActive | null> {
  ensureChannel();
  const cachedNotice = getEffectiveCache()?.notice ?? null;
  if (shouldSkipActionFetchForDismissedBanner(cachedNotice)) {
    return cachedNotice;
  }

  const notice = await refreshReleaseNotice(projectCode, { force: true });
  pollingOptions?.onNotice?.(notice);
  applyMaintenanceRedirect(notice, {
    projectCode,
    mode: pollingOptions?.mode ?? "app",
    homeUrl: pollingOptions?.homeUrl,
  });
  return notice;
}

/** 短防抖：路由监听与显式挂点可能同一操作连打两次 */
let actionNotifyTimer: ReturnType<typeof setTimeout> | null = null;
const ACTION_NOTIFY_DEBOUNCE_MS = 400;

/** fire-and-forget 包装，供事件挂点调用 */
export function notifyReleaseNoticeUserAction(
  projectCode: string = DEFAULT_PROJECT_CODE
): void {
  if (actionNotifyTimer != null) {
    clearTimeout(actionNotifyTimer);
  }
  actionNotifyTimer = setTimeout(() => {
    actionNotifyTimer = null;
    void checkReleaseNoticeOnUserAction(projectCode);
  }, ACTION_NOTIFY_DEBOUNCE_MS);
}

export function startReleaseNoticePolling(
  options: StartReleaseNoticePollingOptions
): () => void {
  ensureChannel();
  ensureLeaderHeartbeat();

  const onVisible = () => {
    if (document.visibilityState !== "visible") return;
    void (async () => {
      if (isLeaderTab()) {
        const notice = await refreshReleaseNotice(options.projectCode, {
          force: shouldForceNetworkRefresh(),
        });
        options.onNotice?.(notice);
        applyMaintenanceRedirect(notice, options);
      } else {
        ensureChannel()?.postMessage({
          type: "sync_request",
        } satisfies ChannelMessage);
        const cached = getEffectiveCache();
        if (cached) {
          options.onNotice?.(cached.notice);
          applyMaintenanceRedirect(cached.notice, options);
        }
      }
      scheduleNextPoll(options.projectCode, options);
    })();
  };

  void (async () => {
    if (isLeaderTab()) {
      const notice = await refreshReleaseNotice(options.projectCode, {
        force: shouldForceNetworkRefresh(),
      });
      options.onNotice?.(notice);
      applyMaintenanceRedirect(notice, options);
    } else {
      const cached = getEffectiveCache();
      if (cached) {
        options.onNotice?.(cached.notice);
        applyMaintenanceRedirect(cached.notice, options);
      }
      ensureChannel()?.postMessage({
        type: "sync_request",
      } satisfies ChannelMessage);
    }
    scheduleNextPoll(options.projectCode, options);
  })();

  document.addEventListener("visibilitychange", onVisible);

  return () => {
    document.removeEventListener("visibilitychange", onVisible);
    if (pollTimer != null) {
      clearTimeout(pollTimer);
      pollTimer = null;
    }
    pollingOptions = null;
    pollingProjectCode = null;
    if (leaderHeartbeatTimer != null) {
      clearInterval(leaderHeartbeatTimer);
      leaderHeartbeatTimer = null;
    }
  };
}
